import {
  INGREDIENT_CATEGORIES,
  INGREDIENT_CATEGORY_LABELS,
  INGREDIENT_DESCRIPTION_MAX_LENGTH,
  INGREDIENT_UNIT_LABELS,
  INGREDIENT_UNITS,
  SEASONS,
  SEASON_LABELS,
} from '@familychef/shared';
import type { IngredientRef, IngredientUnit } from '@familychef/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { Leaf, Plus, Snowflake, Sprout, Sun, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { z } from 'zod';

import { ImageInput } from '../../components/common/ImageInput';

import { logger } from '@/lib/sentry';

const SEASON_ICONS: Record<string, React.ReactNode> = {
  spring: <Sprout size={18} />,
  summer: <Sun size={18} />,
  autumn: <Leaf size={18} />,
  winter: <Snowflake size={18} />,
};

const SEASON_COLORS: Record<string, { active: string; inactive: string }> = {
  spring: {
    active: 'bg-green-600 text-white shadow-sm',
    inactive: 'bg-green-50 text-green-600 hover:bg-green-100',
  },
  summer: {
    active: 'bg-amber-500 text-white shadow-sm',
    inactive: 'bg-amber-50 text-amber-600 hover:bg-amber-100',
  },
  autumn: {
    active: 'bg-orange-500 text-white shadow-sm',
    inactive: 'bg-orange-50 text-orange-600 hover:bg-orange-100',
  },
  winter: {
    active: 'bg-blue-500 text-white shadow-sm',
    inactive: 'bg-blue-50 text-blue-600 hover:bg-blue-100',
  },
};

const MONTHS = [
  'Jan',
  'Fév',
  'Mar',
  'Avr',
  'Mai',
  'Juin',
  'Juil',
  'Août',
  'Sep',
  'Oct',
  'Nov',
  'Déc',
];

const cookingMethodSchema = z.object({
  method: z.string().min(1, 'Requis'),
  temperature: z.string().nullable().optional(),
  duration: z.string().nullable().optional(),
  tips: z.string().nullable().optional(),
});

const formSchema = z.object({
  name: z.string().min(1, 'Le nom est requis'),
  aliases: z.array(z.string()),
  image_url: z.string().nullable().optional(),
  category: z.enum([
    'produce',
    'meat',
    'fish',
    'dairy',
    'bakery',
    'grocery',
    'frozen',
    'canned',
    'spices',
    'beverages',
    'other',
  ]),
  seasons: z.array(z.enum(['spring', 'summer', 'autumn', 'winter'])),
  months: z.array(z.number().int().min(1).max(12)),
  description: z
    .string()
    .max(
      INGREDIENT_DESCRIPTION_MAX_LENGTH,
      `${INGREDIENT_DESCRIPTION_MAX_LENGTH} caractères au plus`,
    )
    .nullable()
    .optional(),
  cooking_methods: z.array(cookingMethodSchema).nullable().optional(),
  preparation_tips: z.array(z.string()),
  conservation: z
    .object({
      fridge: z
        .object({
          duration: z.string().nullable().optional(),
          tips: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      freezer: z
        .object({
          duration: z.string().nullable().optional(),
          tips: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      pantry: z
        .object({
          duration: z.string().nullable().optional(),
          tips: z.string().nullable().optional(),
        })
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
  general_tips: z.array(z.string()),
  density_g_per_ml: z.number().positive().nullable().optional(),
  tablespoon_g: z.number().positive().nullable().optional(),
  cup_g: z.number().positive().nullable().optional(),
  preferred_units: z.array(z.string()).default([]),
  typical_quantities: z.array(z.number().positive()).default([]),
});

type FormData = z.infer<typeof formSchema>;

interface IngredientFormProps {
  defaultValues?: Partial<IngredientRef>;
  onSubmit: (data: FormData) => Promise<void>;
  isLoading?: boolean;
  onCancel: () => void;
  mode: 'create' | 'edit';
}

export function IngredientForm({
  defaultValues,
  onSubmit,
  isLoading,
  onCancel,
  mode,
}: IngredientFormProps) {
  const [lastSelectedMonth, setLastSelectedMonth] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultValues?.name ?? '',
      aliases: defaultValues?.aliases ?? [],
      image_url: defaultValues?.image_url ?? null,
      category: defaultValues?.category ?? 'other',
      seasons: defaultValues?.seasons ?? [],
      months: defaultValues?.months ?? [],
      description: defaultValues?.description ?? null,
      cooking_methods: defaultValues?.cooking_methods ?? [],
      preparation_tips: defaultValues?.preparation_tips ?? [],
      conservation: defaultValues?.conservation ?? null,
      general_tips: defaultValues?.general_tips ?? [],
      density_g_per_ml: defaultValues?.density_g_per_ml ?? undefined,
      tablespoon_g: defaultValues?.tablespoon_g ?? undefined,
      cup_g: defaultValues?.cup_g ?? undefined,
      preferred_units: defaultValues?.preferred_units ?? [],
      typical_quantities: defaultValues?.typical_quantities ?? [],
    },
  });

  const {
    fields: methodFields,
    append: appendMethod,
    remove: removeMethod,
  } = useFieldArray({
    control,
    name: 'cooking_methods' as never,
  });

  const watchedSeasons = watch('seasons');
  const watchedMonths = watch('months');
  const watchedPrepTips = watch('preparation_tips');
  const watchedGeneralTips = watch('general_tips');
  const watchedPreferredUnits = watch('preferred_units');
  const watchedTypicalQty = watch('typical_quantities');

  const toggleSeason = useCallback(
    (season: string) => {
      const current = watchedSeasons ?? [];
      const s = season as 'spring' | 'summer' | 'autumn' | 'winter';
      setValue('seasons', current.includes(s) ? current.filter((x) => x !== s) : [...current, s]);
    },
    [watchedSeasons, setValue],
  );

  const toggleMonth = useCallback(
    (month: number, shiftKey: boolean) => {
      const current = watchedMonths ?? [];

      if (shiftKey && lastSelectedMonth !== null) {
        const start = Math.min(month, lastSelectedMonth);
        const end = Math.max(month, lastSelectedMonth);
        const range = Array.from({ length: end - start + 1 }, (_, i) => start + i);
        // Include new range to current without duplicating
        const next = Array.from(new Set([...current, ...range]));
        setValue('months', next);
      } else {
        setValue(
          'months',
          current.includes(month) ? current.filter((x) => x !== month) : [...current, month],
        );
        setLastSelectedMonth(month);
      }
    },
    [watchedMonths, lastSelectedMonth, setValue],
  );

  const toggleUnit = useCallback(
    (u: string) => {
      const current = watchedPreferredUnits ?? [];
      setValue(
        'preferred_units',
        current.includes(u) ? current.filter((x) => x !== u) : [...current, u],
      );
    },
    [watchedPreferredUnits, setValue],
  );

  function addTypicalQty() {
    setValue('typical_quantities', [...(watchedTypicalQty ?? []), 0]);
  }

  function updateTypicalQty(index: number, value: number) {
    const next = [...(watchedTypicalQty ?? [])];
    next[index] = value;
    setValue('typical_quantities', next);
  }

  function removeTypicalQty(index: number) {
    setValue(
      'typical_quantities',
      (watchedTypicalQty ?? []).filter((_, i) => i !== index),
    );
  }

  function addToList(field: 'preparation_tips' | 'general_tips', current: string[]) {
    setValue(field, [...current, '']);
  }

  function updateList(
    field: 'preparation_tips' | 'general_tips',
    index: number,
    value: string,
    current: string[],
  ) {
    const next = [...current];
    next[index] = value;
    setValue(field, next);
  }

  function removeFromList(
    field: 'preparation_tips' | 'general_tips',
    index: number,
    current: string[],
  ) {
    setValue(
      field,
      current.filter((_, i) => i !== index),
    );
  }

  return (
    <form
      id="ingredient-form"
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(onSubmit, (errs) => {
          logger.warn('Form validation errors', {
            errors: errs as unknown as Record<string, unknown>,
          });
          alert(
            'Le formulaire contient des erreurs de validation locales :\n' +
              JSON.stringify(errs, null, 2),
          );
        })(e);
      }}
      className="space-y-8"
    >
      {/* Section de base */}
      <div className="card">
        <h3 className="mb-4 font-semibold text-slate-900">Informations générales</h3>
        <div className="grid gap-4 sm:grid-cols-3 sm:gap-6">
          <div className="sm:row-span-2">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Illustration</label>
            <Controller
              name="image_url"
              control={control}
              render={({ field }) => (
                <ImageInput value={field.value} onChange={field.onChange} bucket="ingredients" />
              )}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Nom *</label>
            <input
              {...register('name')}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
              placeholder="Ex: Tomate"
            />
            {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name.message}</p>}
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Autres noms</label>
            {/* Read on leaving the field: split on every keystroke, a comma
                just typed would vanish before the next name. */}
            <Controller
              name="aliases"
              control={control}
              render={({ field }) => (
                <input
                  defaultValue={field.value.join(', ')}
                  onBlur={(e) => {
                    field.onChange(
                      e.target.value
                        .split(',')
                        .map((alias) => alias.trim())
                        .filter(Boolean),
                    );
                    field.onBlur();
                  }}
                  className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
                  placeholder="Ex: Encornet, Calamar"
                />
              )}
            />
            <p className="mt-1 text-xs text-slate-500">
              Séparés par des virgules. Une recette ou une recherche qui les emploie trouve cet
              ingrédient.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Catégorie *</label>
            <select
              {...register('category')}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
            >
              {INGREDIENT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {INGREDIENT_CATEGORY_LABELS[c]?.fr}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Description</label>
            <textarea
              {...register('description')}
              rows={3}
              maxLength={INGREDIENT_DESCRIPTION_MAX_LENGTH}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-green-500 focus:outline-none"
              placeholder="Description de l'ingrédient..."
            />
            {errors.description && (
              <p className="mt-1 text-xs text-red-500">{errors.description.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Saisons & Mois */}
      <div className="card">
        <h3 className="mb-4 font-semibold text-slate-900">Disponibilité</h3>
        <div className="mb-4">
          <label className="mb-2 block text-sm font-medium text-slate-700">Saisons</label>
          <div className="flex flex-wrap gap-2">
            {SEASONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleSeason(s)}
                title={SEASON_LABELS[s]?.fr}
                className={`flex items-center justify-center rounded-full p-2.5 transition-colors ${
                  watchedSeasons?.includes(s)
                    ? (SEASON_COLORS[s]?.active ?? 'bg-slate-800 text-white shadow-sm')
                    : (SEASON_COLORS[s]?.inactive ??
                      'bg-slate-100 text-slate-600 hover:bg-slate-200')
                }`}
              >
                {SEASON_ICONS[s] ?? SEASON_LABELS[s]?.fr}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Mois de disponibilité
          </label>
          <div className="flex flex-wrap gap-2">
            {MONTHS.map((m, i) => {
              const monthNum = i + 1;
              return (
                <button
                  key={monthNum}
                  type="button"
                  onClick={(e) => toggleMonth(monthNum, e.shiftKey)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    watchedMonths?.includes(monthNum)
                      ? 'bg-green-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {m}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Densité / Conversion */}
      <div className="card">
        <h3 className="mb-4 font-semibold text-slate-900">Données de conversion</h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">
              Densité (g/ml)
            </label>
            <input
              type="number"
              step="0.01"
              {...register('density_g_per_ml', {
                setValueAs: (v: unknown) => {
                  if (v === '' || v === null || v === undefined) return null;
                  const n = Number(v);
                  return Number.isNaN(n) || n <= 0 ? null : n;
                },
              })}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
              placeholder="Ex: 0.85"
            />
            {errors.density_g_per_ml && (
              <p className="mt-1 text-xs text-red-500">{errors.density_g_per_ml.message}</p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">1 c.à.s (g)</label>
            <input
              type="number"
              step="0.1"
              {...register('tablespoon_g', {
                setValueAs: (v: unknown) => {
                  if (v === '' || v === null || v === undefined) return null;
                  const n = Number(v);
                  return Number.isNaN(n) || n <= 0 ? null : n;
                },
              })}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
              placeholder="Ex: 15"
            />
            {errors.tablespoon_g && (
              <p className="mt-1 text-xs text-red-500">{errors.tablespoon_g.message}</p>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">1 cup (g)</label>
            <input
              type="number"
              step="0.1"
              {...register('cup_g', {
                setValueAs: (v: unknown) => {
                  if (v === '' || v === null || v === undefined) return null;
                  const n = Number(v);
                  return Number.isNaN(n) || n <= 0 ? null : n;
                },
              })}
              className="h-10 w-full rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
              placeholder="Ex: 240"
            />
            {errors.cup_g && <p className="mt-1 text-xs text-red-500">{errors.cup_g.message}</p>}
          </div>
        </div>
      </div>

      {/* Liste de courses */}
      <div className="card">
        <h3 className="mb-4 font-semibold text-slate-900">Paramètres liste de courses</h3>

        <div className="mb-6">
          <label className="mb-2 block text-sm font-medium text-slate-700">
            Unités privilégiées
          </label>
          <p className="mb-2 text-xs text-slate-400">
            Sélectionnez les unités mises en avant lors de l'ajout à la liste de courses.
          </p>
          <div className="flex flex-wrap gap-2">
            {INGREDIENT_UNITS.map((u: IngredientUnit) => (
              <button
                key={u}
                type="button"
                onClick={() => toggleUnit(u)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                  watchedPreferredUnits?.includes(u)
                    ? 'bg-green-600 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {INGREDIENT_UNIT_LABELS[u].fr}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="block text-sm font-medium text-slate-700">Quantités typiques</label>
            <button
              type="button"
              onClick={addTypicalQty}
              className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
            >
              <Plus size={14} /> Ajouter
            </button>
          </div>
          <p className="mb-2 text-xs text-slate-400">
            Quantités proposées sous forme de raccourcis lors de l'ajout à la liste de courses.
          </p>
          <div className="flex flex-wrap gap-2">
            {(watchedTypicalQty ?? []).map((q, i) => (
              <div
                key={i}
                className="flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1"
              >
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={q || ''}
                  onChange={(e) => {
                    const n = Number(e.target.value);
                    if (!Number.isNaN(n) && n > 0) updateTypicalQty(i, n);
                  }}
                  className="h-7 w-20 rounded-md border-0 bg-transparent px-1 text-center text-sm focus:outline-none"
                  placeholder="Qté"
                />
                <button
                  type="button"
                  onClick={() => removeTypicalQty(i)}
                  className="text-red-400 hover:text-red-500"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {(watchedTypicalQty ?? []).length === 0 && (
              <p className="text-sm text-slate-400">Aucune quantité typique définie</p>
            )}
          </div>
        </div>
      </div>

      {/* Modes de cuisson */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Modes de cuisson</h3>
          <button
            type="button"
            onClick={() =>
              appendMethod({ method: '', temperature: null, duration: null, tips: null })
            }
            className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
          >
            <Plus size={14} /> Ajouter
          </button>
        </div>
        <div className="space-y-3">
          {methodFields.map((field, index) => (
            <div
              key={field.id}
              className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 p-3 sm:grid-cols-4"
            >
              <input
                {...register(`cooking_methods.${index}.method`)}
                placeholder="Méthode (ex: four)"
                className="h-9 rounded-md border border-slate-200 px-2.5 text-sm focus:border-green-500 focus:outline-none"
              />
              <input
                {...register(`cooking_methods.${index}.temperature`)}
                placeholder="Température"
                className="h-9 rounded-md border border-slate-200 px-2.5 text-sm focus:border-green-500 focus:outline-none"
              />
              <input
                {...register(`cooking_methods.${index}.duration`)}
                placeholder="Durée"
                className="h-9 rounded-md border border-slate-200 px-2.5 text-sm focus:border-green-500 focus:outline-none"
              />
              <div className="flex gap-2">
                <input
                  {...register(`cooking_methods.${index}.tips`)}
                  placeholder="Conseils"
                  className="h-9 flex-1 rounded-md border border-slate-200 px-2.5 text-sm focus:border-green-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => removeMethod(index)}
                  className="text-red-400 hover:text-red-500"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}
          {methodFields.length === 0 && (
            <p className="text-sm text-slate-400">Aucun mode de cuisson ajouté</p>
          )}
        </div>
      </div>

      {/* Conseils préparation */}
      <div className="card">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Conseils de préparation</h3>
          <button
            type="button"
            onClick={() => addToList('preparation_tips', watchedPrepTips ?? [])}
            className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
          >
            <Plus size={14} /> Ajouter
          </button>
        </div>
        <div className="space-y-2">
          {(watchedPrepTips ?? []).map((_, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={watchedPrepTips?.[i] ?? ''}
                onChange={(e) =>
                  updateList('preparation_tips', i, e.target.value, watchedPrepTips ?? [])
                }
                className="h-9 flex-1 rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
                placeholder={`Conseil ${i + 1}`}
              />
              <button
                type="button"
                onClick={() => removeFromList('preparation_tips', i, watchedPrepTips ?? [])}
                className="text-red-400 hover:text-red-500"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Conservation */}
      <div className="card">
        <h3 className="mb-4 font-semibold text-slate-900">Conservation</h3>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {(['fridge', 'freezer', 'pantry'] as const).map((place) => {
            const labels = {
              fridge: '🧊 Réfrigérateur',
              freezer: '❄️ Congélateur',
              pantry: '🗄️ Garde-manger',
            };
            return (
              <div key={place} className="rounded-lg border border-slate-200 p-3">
                <p className="mb-2 text-sm font-medium text-slate-700">{labels[place]}</p>
                <input
                  {...register(`conservation.${place}.duration`)}
                  placeholder="Durée (ex: 3 jours)"
                  className="mb-2 h-8 w-full rounded-md border border-slate-200 px-2.5 text-xs focus:border-green-500 focus:outline-none"
                />
                <input
                  {...register(`conservation.${place}.tips`)}
                  placeholder="Conseils"
                  className="h-8 w-full rounded-md border border-slate-200 px-2.5 text-xs focus:border-green-500 focus:outline-none"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Astuces générales */}
      <div className="card">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Astuces générales</h3>
          <button
            type="button"
            onClick={() => addToList('general_tips', watchedGeneralTips ?? [])}
            className="flex items-center gap-1 text-sm text-green-600 hover:text-green-700"
          >
            <Plus size={14} /> Ajouter
          </button>
        </div>
        <div className="space-y-2">
          {(watchedGeneralTips ?? []).map((_, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={watchedGeneralTips?.[i] ?? ''}
                onChange={(e) =>
                  updateList('general_tips', i, e.target.value, watchedGeneralTips ?? [])
                }
                className="h-9 flex-1 rounded-lg border border-slate-200 px-3 text-sm focus:border-green-500 focus:outline-none"
                placeholder={`Astuce ${i + 1}`}
              />
              <button
                type="button"
                onClick={() => removeFromList('general_tips', i, watchedGeneralTips ?? [])}
                className="text-red-400 hover:text-red-500"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3 pb-8">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-slate-200 bg-white px-5 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="rounded-lg bg-green-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700 disabled:opacity-60"
        >
          {isLoading
            ? 'Enregistrement...'
            : mode === 'create'
              ? "Créer l'ingrédient"
              : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
}
